/**
 * Specialty Drug Watchlist
 *
 * High-cost specialty drugs commonly reimbursed under Medicare Part B / Part D
 * that are associated with adherence challenges (injection schedule, oral
 * compliance, prior-auth gaps, high co-pay abandonment).
 *
 * Provenance
 * ----------
 * This list was compiled from publicly available Medicare Part B and Part D
 * spending data published by CMS (cms.gov), cross-referenced with:
 *   - CMS Medicare Part B Drug Spending Dashboard (2023)
 *   - CMS Medicare Part D Drug Spending Dashboard (2023)
 *   - ASCO/NCCN treatment guidelines for common oncology indications
 *
 * Each entry uses the brand name (lowercase) as the match key. The matching
 * logic in riskRules.js does a case-insensitive substring check against the
 * patient's medications field, so "Lupron Depot 22.5mg" will match "lupron".
 *
 * Maintenance
 * -----------
 * Clinics can add or remove drugs from the list via the Settings > Rules UI.
 * To update the *default* list shipped to new clinics, edit the arrays below
 * and redeploy. Keep entries lowercase.
 */

// -- Prostate cancer: androgen deprivation & androgen receptor inhibitors --
const PROSTATE = [
  'lupron',     // leuprolide — GnRH agonist (Part B)
  'eligard',    // leuprolide — GnRH agonist (Part B)
  'firmagon',   // degarelix  — GnRH antagonist (Part B)
  'orgovyx',    // relugolix  — oral GnRH antagonist (Part D)
  'xtandi',     // enzalutamide — androgen receptor inhibitor (Part D)
  'zytiga',     // abiraterone  — CYP17 inhibitor (Part D)
  'erleada',    // apalutamide  — androgen receptor inhibitor (Part D)
  'nubeqa',     // darolutamide — androgen receptor inhibitor (Part D)
];

// -- Prostate cancer: advanced / cellular therapies --
const PROSTATE_ADVANCED = [
  'provenge',   // sipuleucel-T — autologous cellular immunotherapy (Part B)
  'xofigo',     // radium-223   — alpha-emitting radiopharmaceutical (Part B)
];

// -- Immune checkpoint inhibitors (multiple solid tumors) --
const CHECKPOINT_INHIBITORS = [
  'keytruda',   // pembrolizumab — anti-PD-1 (Part B)
  'opdivo',     // nivolumab     — anti-PD-1 (Part B)
  'tecentriq',  // atezolizumab  — anti-PD-L1 (Part B)
  'imfinzi',    // durvalumab    — anti-PD-L1 (Part B)
  'bavencio',   // avelumab      — anti-PD-L1 (Part B)
];

// -- Hematologic malignancies (myeloma, lymphoma) --
const HEMATOLOGIC = [
  'revlimid',   // lenalidomide  — immunomodulator (Part D)
  'pomalyst',   // pomalidomide  — immunomodulator (Part D)
  'velcade',    // bortezomib    — proteasome inhibitor (Part B)
  'darzalex',   // daratumumab   — anti-CD38 monoclonal antibody (Part B)
];

// -- CDK4/6 inhibitors (breast cancer) --
const CDK_INHIBITORS = [
  'ibrance',    // palbociclib   — CDK4/6 inhibitor (Part D)
  'kisqali',    // ribociclib    — CDK4/6 inhibitor (Part D)
  'verzenio',   // abemaciclib   — CDK4/6 inhibitor (Part D)
];

/**
 * Combined default watchlist (22 drugs).
 * Exported as the single source of truth used by riskRules.js and
 * useClinicRules.js for the med_adherence_risk rule default config.
 */
export const DEFAULT_SPECIALTY_DRUGS = [
  ...PROSTATE,
  ...PROSTATE_ADVANCED,
  ...CHECKPOINT_INHIBITORS,
  ...HEMATOLOGIC,
  ...CDK_INHIBITORS,
];
